import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * HelpTooltip — pictograma „?" care afișează o scurtă explicație
 * și un link „Apasă aici pentru instrucțiuni complete" către manual.
 *
 * Props:
 *   - text:    string (opțional) — textul scurt afișat în popover
 *   - section: string (opțional) — id-ul secțiunii din manual;
 *              dacă e setat, popover-ul arată linkul către /manual#<section>
 *   - position: 'top' | 'bottom' | 'left' | 'right' (default: 'bottom')
 *   - size:    number — dimensiunea iconiței în pixeli (default: 16)
 */
const HelpTooltip = ({ text, section, position = 'bottom', size = 16, className = '' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Închide la click în afara popover-ului.
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const positionClasses = {
    top: 'bottom-full mb-2 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2',
    left: 'right-full mr-2 top-1/2 -translate-y-1/2',
    right: 'left-full ml-2 top-1/2 -translate-y-1/2',
  };

  return (
    <span className={`relative inline-flex ${className}`} ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onMouseEnter={() => setOpen(true)}
        aria-label={t('help.ariaLabel') || 'Ajutor'}
        className="inline-flex items-center justify-center rounded-full text-primary-600 dark:text-primary-300 hover:text-primary-800 dark:hover:text-primary-200 hover:bg-primary-50 dark:hover:bg-primary-950/40 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
        style={{ width: size + 4, height: size + 4 }}
      >
        <svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="12" cy="12" r="10" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.879 8.879a3 3 0 114.242 0c-.878.878-2.121 1.121-2.121 2.621V12"
          />
          <circle cx="12" cy="16" r="0.5" fill="currentColor" />
        </svg>
      </button>
      {open && (
        <div
          role="tooltip"
          onMouseLeave={() => setOpen(false)}
          className={`absolute ${positionClasses[position]} z-40 w-72 max-w-[90vw] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg p-3 text-sm text-slate-700 dark:text-slate-200`}
        >
          {text && <p className="mb-2 leading-relaxed">{text}</p>}
          {section && (
            <Link
              to={`/manual#${section}`}
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-300 hover:text-primary-800 dark:hover:text-primary-200 font-medium"
            >
              {t('help.readMore') || 'Apasă aici pentru instrucțiuni complete'}
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                />
              </svg>
            </Link>
          )}
        </div>
      )}
    </span>
  );
};

export default HelpTooltip;
